// v10/src/components/SkipReminder.js - REDESIGNED
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ArrowLeft } from 'lucide-react';

const SkipReminder = ({
  message = '',
  onDismiss = () => {},
  onGoBackToItem = () => {},
  autoDismiss = true,
  skippedItem = null,
  darkMode
}) => {
  useEffect(() => {
    if (autoDismiss) {
      const timer = setTimeout(() => onDismiss(), 5000);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, onDismiss]);

  if (!message) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -80, scale: 0.88 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -40, scale: 0.92 }}
      transition={{ type: 'spring', stiffness: 160, damping: 18 }}
      style={{
        position: 'fixed', top: '16px', left: '16px', right: '16px',
        zIndex: 50, maxWidth: '400px', margin: '0 auto',
        borderRadius: '22px', overflow: 'hidden',
        background: darkMode
          ? 'rgba(20, 10, 2, 0.92)'
          : 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 16px 48px rgba(239,68,68,0.22), 0 0 0 1px rgba(245,158,11,0.25)',
        border: darkMode ? '1px solid rgba(245,158,11,0.2)' : '1px solid rgba(245,158,11,0.3)',
      }}
    >
      {/* Animated top stripe */}
      <motion.div
        animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
        style={{
          height: '3px',
          background: 'linear-gradient(90deg, #f59e0b, #ef4444, #f97316, #ef4444, #f59e0b)',
          backgroundSize: '300% 100%',
        }}
      />

      <div style={{ padding: '14px 16px 16px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Badge */}
            <motion.div
              animate={{ rotate: [0, -8, 8, -8, 8, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2 }}
              style={{
                width: '38px', height: '38px', borderRadius: '12px', flexShrink: 0,
                background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(239,68,68,0.35)',
                fontSize: '18px',
              }}
            >
              ⚠️
            </motion.div>

            <div>
              <p style={{
                margin: 0, fontWeight: 800, fontSize: '14px', lineHeight: 1.2,
                color: darkMode ? 'rgba(255,255,255,0.92)' : '#0f172a',
              }}>
                ¡No olvides chequear!
              </p>
              <motion.p
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{
                  margin: 0, fontSize: '11px', fontWeight: 600, marginTop: '2px',
                  color: darkMode ? 'rgba(251,191,36,0.7)' : 'rgba(217,119,6,0.8)',
                  fontFamily: 'monospace', letterSpacing: '0.05em',
                }}
              >
                ÍTEM PENDIENTE
              </motion.p>
            </div>
          </div>

          <motion.button
            onClick={onDismiss}
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            style={{
              width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
              background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
              transition: 'background 0.15s',
            }}
          >
            <X style={{ width: '14px', height: '14px' }} />
          </motion.button>
        </div>

        {/* Message */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          style={{
            padding: '10px 12px', borderRadius: '12px', marginBottom: '12px',
            background: darkMode ? 'rgba(245,158,11,0.07)' : 'rgba(254,243,199,0.7)',
            border: darkMode ? '1px solid rgba(245,158,11,0.15)' : '1px solid rgba(245,158,11,0.25)',
          }}
        >
          <p style={{
            margin: 0, fontSize: '13px', fontWeight: 500, lineHeight: '1.45',
            color: darkMode ? 'rgba(255,255,255,0.75)' : '#78350f',
          }}>
            {message}
          </p>
        </motion.div>

        {/* Back button */}
        {skippedItem && (
          <motion.button
            onClick={() => { onGoBackToItem(skippedItem); onDismiss(); }}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.97 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            style={{
              width: '100%', padding: '12px 16px',
              background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
              color: 'white', border: 'none', borderRadius: '14px',
              fontWeight: 700, fontSize: '14px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              boxShadow: '0 4px 16px rgba(239,68,68,0.3)',
            }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Volver a este ítem
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default SkipReminder;
